import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Wind from "components/icons/Wind";
import { BULK_INVOICE_ACTION, BULK_INVOICE_TYPE } from "constants/invoicing";
import { useBillBulkAction } from "hooks/useBillBulkAction";
import { useToast } from "hooks/useToast";
import { RowSelectionState } from "react-table-8.10.7";
import { useBulkUpdateInvoiceMutation } from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { BillTable } from "./BillTable";

export default function BillMarkAsActiveModal({
  groupId,
  entityId,
  selectedRows,
  rowSelection,
  success,
}: {
  groupId: string;
  entityId: string;
  selectedRows: string[];
  rowSelection?: RowSelectionState;
  success: () => void;
}) {
  const [bulkUpdateOrDeleteBill, { isLoading: isMarkingBillsAsActive }] =
    useBulkUpdateInvoiceMutation();
  const { alertToast, successToast } = useToast();

  const {
    allBills,
    isLoading,
    rowSelectionState,
    setRowSelectionState,
    newSelectedRows,
    valid,
    invalid,
    totalBills,
    isAllBillInvalid,
    shouldShowButton,
  } = useBillBulkAction({
    groupId,
    entityId,
    selectedRows,
    rowSelection,
    actionType: "markAsActive",
  });

  const onChange = async (rowIds?: string[]) => {
    try {
      if (!rowIds || rowIds.length < 1) return;
      await bulkUpdateOrDeleteBill({
        entityId: entityId!,
        groupId: groupId!,
        record_ids: rowIds,
        action: BULK_INVOICE_ACTION.MARK_AS_ACTIVE,
        type: BULK_INVOICE_TYPE.BILL,
      }).unwrap();
      successToast({
        message: `Selected bills marked as active.`,
      });
      success();
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError)?.data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title>Mark bills as active</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <Async.Root
          isLoading={isLoading}
          isSuccess={!isLoading}
          isEmpty={false}
        >
          <Async.Success>
            {newSelectedRows.length > 0 && (
              <>
                {isAllBillInvalid && (
                  <div className="t-flex t-items-center t-gap-2 t-flex-col t-pb-12">
                    <Wind className="t-text-neutral-40" />
                    The{" "}
                    {pluralize(
                      newSelectedRows.length,
                      "bill is",
                      "bills are"
                    )}{" "}
                    already active and hence cannot be updated.
                  </div>
                )}

                {valid.length > 0 && (
                  <>
                    <div className="t-mb-4">
                      Are you sure you want to mark{" "}
                      <b>
                        {valid.length} out of {totalBills} bills{" "}
                      </b>{" "}
                      as active? This action cannot be undone.
                    </div>
                    {invalid.length > 0 && (
                      <div className="t-mb-4">
                        <Banner
                          type="warning"
                          description={
                            <span>
                              The remaining{" "}
                              {pluralize(
                                invalid.length,
                                "bill is",
                                "bills are"
                              )}{" "}
                              already active and hence can not be updated.
                            </span>
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            <BillTable
              billsData={allBills?.bills || []}
              disabledRowIds={invalid.map((row) => row?.uuid)}
              rowSelection={rowSelectionState}
              setRowSelection={setRowSelectionState}
              showActionsColumn={false}
            />
          </Async.Success>
        </Async.Root>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button>Cancel</Button>
        </Modal.RawClose>
        {shouldShowButton && (
          <Button
            customType="primary"
            onClick={() => onChange(valid.map((row) => row.uuid))}
            isLoading={isMarkingBillsAsActive}
            disabled={isMarkingBillsAsActive}
          >
            Mark as Active
          </Button>
        )}
      </Modal.FooterButtonGroup>
    </>
  );
}
